import React, { forwardRef } from 'react';
import { PropTypes } from '../../../utils/propTypes';
import { buttonVariants } from '../Button2';
import styles from './InputGroup.module.css';

/**
 * InputGroup — wraps an input/textarea with addons (icons, text, buttons) inside
 * one bordered surface. Reimplemented in Athena's idiom (JSX + CSS Module + 2.0
 * tokens, flat). The GROUP owns the border + focus ring (`:focus-within`); the
 * inner control is borderless/transparent so the group is the single focus
 * surface. `InputGroupButton` reuses Button2's `buttonVariants` (no redefinition).
 *
 *   <InputGroup>
 *     <InputGroupInput placeholder="Search…" />
 *     <InputGroupAddon align="inline-end"><SearchIcon /></InputGroupAddon>
 *   </InputGroup>
 */

// InputGroup — the bordered wrapper + focus surface.
const InputGroup = ({ className = '', children, ...props }) => (
  <div data-slot="input-group" className={`${styles.group} ${className}`} {...props}>
    {children}
  </div>
);

// InputGroupInput — borderless input; the group provides the chrome.
// forwardRef so consumers can focus it (e.g. FormModal autofocus).
const InputGroupInput = forwardRef(({ className = '', type = 'text', ...props }, ref) => (
  <input
    ref={ref}
    type={type}
    data-slot="input-group-control"
    className={`${styles.control} ${className}`}
    {...props}
  />
));
InputGroupInput.displayName = 'InputGroupInput';

// InputGroupTextarea — borderless textarea variant (self-contained).
const InputGroupTextarea = ({ className = '', rows = 3, ...props }) => (
  <textarea
    rows={rows}
    data-slot="input-group-control"
    className={`${styles.control} ${styles.textarea} ${className}`}
    {...props}
  />
);

// InputGroupAddon — icons / text / buttons positioned around the control.
// `align` is visual only; place the addon AFTER the control in the DOM for focus.
const InputGroupAddon = ({ align = 'inline-start', className = '', children, ...props }) => {
  const alignClass = {
    'inline-start': styles.inlineStart,
    'inline-end': styles.inlineEnd,
    'block-start': styles.blockStart,
    'block-end': styles.blockEnd,
  }[align];
  return (
    <div data-slot="input-group-addon" className={`${styles.addon} ${alignClass} ${className}`} {...props}>
      {children}
    </div>
  );
};

// InputGroupButton — small button inside the group; reuses Button2 styling.
const InputGroupButton = ({ size = 'xs', variant = 'ghost', className = '', type = 'button', children, ...props }) => (
  <button type={type} className={buttonVariants({ variant, size, className })} {...props}>
    {children}
  </button>
);

// InputGroupText — muted inline text inside the group.
const InputGroupText = ({ className = '', children, ...props }) => (
  <span data-slot="input-group-text" className={`${styles.text} ${className}`} {...props}>
    {children}
  </span>
);

InputGroup.propTypes = { className: PropTypes.string, children: PropTypes.node };
InputGroupInput.propTypes = { className: PropTypes.string, type: PropTypes.string };
InputGroupTextarea.propTypes = { className: PropTypes.string, rows: PropTypes.number };
InputGroupAddon.propTypes = { align: PropTypes.string, className: PropTypes.string, children: PropTypes.node };
InputGroupButton.propTypes = {
  size: PropTypes.string,
  variant: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.node,
};
InputGroupText.propTypes = { className: PropTypes.string, children: PropTypes.node };

export {
  InputGroup,
  InputGroupInput,
  InputGroupTextarea,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
};
